// ----------------------------------------------------------------------
// iOS software keyboard handling.
//
// The bug this fixes: on iOS, tapping straight from one field to the next
// leaves the new field sitting behind the keyboard. It happens on every form
// with more than one field, and only with Apple's keyboard.
//
// Why. Tapping field B blurs field A first. iOS reads that blur as the keyboard
// closing, so WKWebView undoes the scroll it had applied to hold the page above
// the keyboard and the page drops back down. A few milliseconds later field B
// takes focus and the keyboard re-presents, but WebKit only puts its inset back
// - it does not redo the lift, because from its side the keyboard never left.
// The page stays low and the field is under the keyboard. Gboard is an
// out-of-process keyboard extension that never signals a hide on a focus
// change, so the undo never happens there. Moving with the keyboard's own next
// arrow does not blur-then-tap either, which is why that path always felt fine.
//
// What it actually does, measured on device rather than guessed at:
//
//   1. Undo the illegal scroll. With the keyboard up the document reported
//      scrollTop -380 while scrollHeight and clientHeight were both 417, so the
//      scroll range was [0, 0] and WebKit had driven the page 380px outside it.
//      Nothing brings that back on its own. This is the fix that matters in the
//      Flutter WebView.
//   2. If the field is genuinely covered, scroll it into view. This is the
//      mobile Safari path, where the web view stays full height and only the
//      visual viewport shrinks. In the app the Scaffold resizes the web view,
//      so this branch never runs and does not need to.
//
// Deliberately boring, because the elaborate version was worse. Two rules:
//   1. Never compute a scroll offset. Ask the browser with scrollIntoView, which
//      picks the right scroll container and clamps itself. Doing our own
//      scrollTop arithmetic off a misread viewport is what ran the document to
//      its end and parked the field on the keyboard.
//   2. Never touch layout. No CSS variables, no padding, no height overrides.
//      Sizing the page to the visual viewport is what produced a white band
//      above the keyboard with the app squeezed into a strip.
//
// It reads the visual viewport only to answer "is the field covered", so if
// those numbers turn out to be unreliable on some device the answer is "not
// covered", it does nothing, and behaviour falls back to exactly what it is
// today. It cannot make things worse than baseline.
//
// No-op off iOS/iPadOS, so desktop and Android are untouched.
// ----------------------------------------------------------------------

// iPadOS reports itself as a Mac, so touch points are what separate it from a
// desktop Safari that also has visualViewport.
function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
}

// Below this, the missing height is the URL bar or a rounding artefact rather
// than a keyboard, and there is nothing to get out of the way of.
const KEYBOARD_MIN_INSET = 90;
// Breathing room kept between the field's edges and the visible area, so a
// field that is only just on screen still counts as covered and gets moved.
const MARGIN = 16;
// Roughly the keyboard's present animation. The first check.
const SETTLE_MS = 300;
// Second check. Apple's keyboard grows after it arrives - the QuickType strip,
// the Passwords bar on a password field, the "From Messages" bar on a one-time
// code field - and each of those eats more of the screen after the first check
// has already passed. Gboard holds one height, which is part of why it never
// showed this.
const RECHECK_MS = 550;

function isTextField(el) {
  if (!el || el.nodeType !== 1) return false;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (el.isContentEditable) return true;
  if (tag !== 'INPUT') return false;
  // Buttons and checkboxes do not raise a keyboard.
  return !/^(button|submit|reset|checkbox|radio|file|image|range|color)$/i.test(el.type || 'text');
}

// Kill switch. ?kbfix=0 disables the module for the rest of the session, so a
// device can be put back to baseline without a deploy.
function isDisabled() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('kbfix')) {
      const off = params.get('kbfix') === '0';
      window.sessionStorage.setItem('kbfix-off', off ? '1' : '0');
      return off;
    }
    return window.sessionStorage.getItem('kbfix-off') === '1';
  } catch (e) {
    return false;
  }
}

function debugRequested() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('kbdebug')) {
      const on = params.get('kbdebug') !== '0';
      window.sessionStorage.setItem('kbdebug', on ? '1' : '0');
      return on;
    }
    return window.sessionStorage.getItem('kbdebug') === '1';
  } catch (e) {
    return false;
  }
}

// On-screen readout of the numbers this depends on. There is no console in a
// device WebView, and guessing at these is what produced the earlier failures.
function mountDebugReadout() {
  const vv = window.visualViewport;
  const box = document.createElement('div');
  box.setAttribute('style', [
    'position:fixed', 'top:0', 'left:0', 'z-index:2147483647',
    'background:rgba(0,0,0,.82)', 'color:#0f0', 'font:11px/1.35 monospace',
    'padding:4px 6px', 'white-space:pre', 'pointer-events:none',
    'max-width:100%',
  ].join(';'));
  document.body.appendChild(box);

  const paint = () => {
    const el = document.activeElement;
    const rect = el && el.getBoundingClientRect ? el.getBoundingClientRect() : null;
    const scroller = document.scrollingElement || document.documentElement;
    box.textContent = [
      `innerH   ${window.innerHeight}`,
      `vv.h     ${Math.round(vv.height)}`,
      `vv.top   ${Math.round(vv.offsetTop)}`,
      `vv.scale ${vv.scale.toFixed(2)}`,
      `scrollY  ${Math.round(scroller.scrollTop)} / ${scroller.scrollHeight}`,
      `active   ${el ? el.tagName : '-'} bottom ${rect ? Math.round(rect.bottom) : '-'}`,
    ].join('\n');
  };

  paint();
  vv.addEventListener('resize', paint);
  vv.addEventListener('scroll', paint);
  window.setInterval(paint, 300);
}

export default function initIOSKeyboardViewport() {
  if (typeof window === 'undefined' || !window.visualViewport || !isIOS()) {
    return () => {};
  }

  if (debugRequested()) {
    if (document.body) mountDebugReadout();
    else window.addEventListener('DOMContentLoaded', mountDebugReadout, { once: true });
  }

  if (isDisabled()) {
    return () => {};
  }

  const vv = window.visualViewport;
  let timers = [];

  const clearTimers = () => {
    timers.forEach(window.clearTimeout);
    timers = [];
  };

  // The actual fault, measured on device: with the keyboard up the document
  // reported scrollTop -380 while scrollHeight and clientHeight were both 417.
  // The scroll range is [0, 0] - html, body and #root are all height:100%, so
  // the document is exactly viewport tall and has nothing to scroll - and
  // WebKit had still driven it 380px into an offset that is not legal. Nothing
  // brings that back on its own, so the page just sits shoved down with the
  // focused field below the bottom edge.
  //
  // A scrollTop outside [0, scrollHeight - clientHeight] is never legitimate,
  // so this needs no keyboard detection and cannot misfire: if the number is in
  // range, which it is at every normal moment, nothing happens.
  const clampScroll = () => {
    const s = document.scrollingElement || document.documentElement;
    if (!s) return;
    const max = Math.max(0, s.scrollHeight - s.clientHeight);
    const clamped = Math.min(Math.max(s.scrollTop, 0), max);
    if (clamped !== s.scrollTop) s.scrollTop = clamped;
  };

  const ensureVisible = (el) => {
    clampScroll();

    // Only ever act on the field the member is actually in. Focus may have
    // moved on, or the field may have been unmounted, between scheduling this
    // and running it.
    if (!el || !el.isConnected || document.activeElement !== el) return;
    // A pinch zoom shrinks the visual viewport too, and moving the page under
    // someone who is zoomed in is worse than leaving it alone.
    if (vv.scale > 1.05) return;

    // How much of the screen the keyboard has taken, as the visual viewport
    // sees it. This is the mobile Safari case, where the web view stays full
    // height and only the visual viewport shrinks.
    //
    // Inside the Flutter WebView this is always 0: the Scaffold resizes the web
    // view itself, so innerHeight already excludes the keyboard and there is
    // nothing left for the visual viewport to report. Measured on device:
    // innerH 462 / vv.h 462 with the number pad up, innerH 417 / vv.h 417 with
    // the alphabetic keyboard up. So in the app the clamp above is the fix and
    // everything below is a no-op, which is correct - a resized web view means
    // the field is already in view.
    const covered = window.innerHeight - vv.height;
    if (covered < KEYBOARD_MIN_INSET) return;

    // getBoundingClientRect and vv.offsetTop are both in layout viewport
    // coordinates, so they compare directly. offsetTop is how far WebKit has
    // dragged the visual viewport, which is not always zero.
    const visibleTop = vv.offsetTop;
    const visibleBottom = vv.offsetTop + vv.height;
    const rect = el.getBoundingClientRect();
    const fits = rect.top >= visibleTop + MARGIN && rect.bottom <= visibleBottom - MARGIN;
    if (fits) return;

    // Let the browser do the scrolling. It knows which container scrolls and it
    // clamps to that container's range, so this cannot overshoot. 'center'
    // leaves room for the field's label and any helper text beneath it.
    el.scrollIntoView({ block: 'center', inline: 'nearest' });
  };

  const check = (el) => {
    clearTimers();
    timers.push(window.setTimeout(() => ensureVisible(el), SETTLE_MS));
    timers.push(window.setTimeout(() => ensureVisible(el), RECHECK_MS));
  };

  const onFocusIn = (e) => {
    if (!isTextField(e.target)) {
      clearTimers();
      return;
    }
    check(e.target);
  };

  // The keyboard changing size while a field is already focused covers the
  // field just as effectively as focusing a new one.
  const onViewportResize = () => {
    clampScroll();
    const el = document.activeElement;
    if (!isTextField(el)) return;
    check(el);
  };

  // The most direct signal there is. vv.offsetTop went to -380 at the exact
  // moment the page was displaced, in lockstep with scrollTop, so catching the
  // visual viewport moving out of place undoes it immediately rather than up to
  // half a second later. Re-clamping is idempotent: the assignment fires
  // another scroll event, that one reads in range, and it stops.
  const onViewportScroll = () => {
    clampScroll();
  };

  document.addEventListener('focusin', onFocusIn, true);
  vv.addEventListener('resize', onViewportResize);
  vv.addEventListener('scroll', onViewportScroll);

  return () => {
    document.removeEventListener('focusin', onFocusIn, true);
    vv.removeEventListener('resize', onViewportResize);
    vv.removeEventListener('scroll', onViewportScroll);
    clearTimers();
  };
}
